import { Box, IconButton, List, ListItem, Stack, Typography } from '@mui/material'
import type { FC } from 'react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { To } from 'react-router'
import { useNavigate } from 'react-router'

import type { DestinationDto } from '@hcr/api/optimizely'
import { COLORS, DividerWithLabel, ICONS, IconFixedSize, remUnit, unit } from '@hcr/ui'
import { flow, getPropertyValue, group, isEmpty, isNotUndefined, sort } from '@hcr/utils'

import { useHoliday, useLastPath } from '../../../contexts'
import { useDestinationsQuery } from '../../../hooks'
import { Path } from '../../../models'
import { compareStringsIntl, isServicesPath, to } from '../../../utils'
import { LayoutBase, LayoutNavigationBackError500, Metadata } from '../../common'

import { Destination } from './common'
import { ContextMenuDestinationsLoading } from './ContextMenuDestinationsLoading'

export const ContextMenuDestinations: FC = () => {
  const { t } = useTranslation()
  const lastPath = useLastPath()
  const navigate = useNavigate()
  const holiday = useHoliday()

  const destinations = useDestinationsQuery({
    select: flow(
      getPropertyValue('destinations'),
      sort({ comparer: compareStringsIntl, asc: getPropertyValue('title') }),
      group(({ isSpa }) => (isSpa ? 'isSpa' : 'isNotSpa'))
    ),
  })

  const getBackNavigationPath = useCallback(
    (destinationId?: string): To => {
      if (isNotUndefined(destinationId) && isServicesPath(lastPath.root)) {
        return to([Path.Services, { destinationId }])
      }

      return lastPath.root
    },
    [lastPath.root]
  )

  const createDestination = useCallback(
    (destination: DestinationDto) => (
      <ListItem
        sx={{
          '&:not(:last-of-type)': {
            borderBottom: `thin solid ${COLORS.vaalea[600]}`,
          },
        }}
        disablePadding
        key={destination.destinationId}
      >
        <Destination
          description={destination.description}
          isActive={destination.destinationId === holiday.data.destinationId}
          onClick={() => {
            holiday.setDestinationId(destination.destinationId)
            void navigate(getBackNavigationPath(destination.destinationId))
          }}
          title={destination.title}
        />
      </ListItem>
    ),
    [getBackNavigationPath, holiday, navigate]
  )

  if (destinations.isError || (destinations.isSuccess && isEmpty(destinations.data))) {
    return <LayoutNavigationBackError500 to={getBackNavigationPath()} />
  }

  return (
    <LayoutBase>
      <Metadata title={t('page-title.context-menu-destinations')} />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: COLORS.vaalea['60%'],
        }}
      >
        <Box
          component='header'
          sx={{
            backgroundColor: COLORS.vaalea['60%'],
            padding: `0 0 ${unit(3.5)} ${unit(7)}`,
          }}
        >
          <Stack paddingX={unit(3)} paddingY={unit(2)}>
            <IconButton
              onClick={() => navigate(getBackNavigationPath())}
              aria-label={t('context-menu.close')}
              sx={{
                height: unit(12),
                width: unit(12),
                alignSelf: 'end',
                fontSize: remUnit(6),
              }}
            >
              <IconFixedSize icon={ICONS.farXmark} aria-hidden='true' />
            </IconButton>
          </Stack>
          <Typography component='h1' variant='uppercaseS'>
            {t('context-menu.holiday-club-destinations')}
          </Typography>
        </Box>
        <main>
          {destinations.isSuccess ? (
            <>
              {destinations.data.has('isSpa') && (
                <>
                  <DividerWithLabel>{t('context-menu.spa-hotels-holiday-houses-and-apartments')}</DividerWithLabel>
                  <List disablePadding>{destinations.data.get('isSpa')?.map(createDestination)}</List>
                </>
              )}
              {destinations.data.has('isNotSpa') && (
                <>
                  <DividerWithLabel>{t('context-menu.holiday-houses-and-apartments')}</DividerWithLabel>
                  <List disablePadding>{destinations.data.get('isNotSpa')?.map(createDestination)}</List>
                </>
              )}
            </>
          ) : (
            <ContextMenuDestinationsLoading />
          )}
        </main>
      </Box>
    </LayoutBase>
  )
}
